/**
 * Error Boundary Component
 * Catches React rendering errors and provides recovery options.
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import styles from './ErrorBoundary.module.css';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Fallback UI to render on error */
  fallback?: ReactNode;
  /** Called when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Whether to show retry button (default: true) */
  showRetry?: boolean;
  /** Custom retry button text */
  retryLabel?: string;
  /** Feature name for context in error message */
  featureName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    this.props.onError?.(error, errorInfo);

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary] Caught error:', error);
      console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
    }
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const {
      children,
      fallback,
      showRetry = true,
      retryLabel = 'Try Again',
      featureName,
    } = this.props;

    if (hasError) {
      // Custom fallback provided
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div className={styles.container} role="alert">
          <div className={styles.icon}>
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h2 className={styles.title}>Something went wrong</h2>
          <p className={styles.message}>
            {featureName
              ? `There was a problem loading ${featureName}.`
              : 'An unexpected error occurred.'}
          </p>
          {import.meta.env.DEV && error && (
            <pre className={styles.errorDetails}>{error.message}</pre>
          )}
          <div className={styles.actions}>
            {showRetry && (
              <button className={styles.retryButton} onClick={this.handleRetry} type="button">
                {retryLabel}
              </button>
            )}
            <button className={styles.reloadButton} onClick={this.handleReload} type="button">
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return children;
  }
}
